import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import Modal, { ModalFooter, Prompt } from '@ingka/modal';
import Button from '@ingka/button';
import useTranslation from '../../hooks/useTranslation';

const StyledModal = styled(Modal)`
  .prompt {
    max-height: 90vh;
    overflow-y: auto;
  }

  .prompt__content-wrapper {
    padding: 24px 10px 4px 10px;
    max-height: 85vh;
    max-width: 85vw;
  }

  .prompt__heading {
    margin-left: -5px;
  }
`;

const StyledConfirmButton = styled(Button)`
  &&& .btn__inner {
    background: #111111;
    color: #ffffff;
    border-color: #111111;
  }

  &&&:hover .btn__inner {
    background: #333333;
    border-color: #333333;
  }
`;

interface OrderConfirmationModalProps {
  visible: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const OrderConfirmationModal: React.FC<OrderConfirmationModalProps> = ({
  visible,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLElement | null>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const wasVisibleRef = useRef(false);

  // Capture focus on the closed→open transition (not during render)
  useEffect(() => {
    if (visible && !wasVisibleRef.current) {
      triggerRef.current = document.activeElement as HTMLElement;
    }
    wasVisibleRef.current = visible;
  }, [visible]);

  const handleClose = (e?: React.SyntheticEvent): void => {
    if (e) e.preventDefault();
    onCancel();
  };

  const onModalClosed = (): void => {
    triggerRef.current?.focus();
  };

  useEffect(() => {
    if (visible) {
      // Delay to allow modal DOM to render before focusing
      const timer = setTimeout(() => {
        confirmBtnRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [visible]);

  const footer = (
    <ModalFooter>
      <Button
        type="secondary"
        text={t('orderForm.orderConfirmationModal.cancelButton')}
        onClick={handleClose}
      />
      <StyledConfirmButton
        text={t('orderForm.orderConfirmationModal.confirmButton')}
        onClick={onConfirm}
        ref={
          confirmBtnRef as unknown as React.Ref<
            HTMLButtonElement & HTMLAnchorElement
          >
        }
      />
    </ModalFooter>
  );

  return (
    <StyledModal
      visible={visible}
      handleCloseBtn={handleClose}
      onModalClosed={onModalClosed}
    >
      <Prompt
        title={t('orderForm.orderConfirmationModal.title')}
        titleId="order-confirmation-title"
        footer={footer}
        closeBtnClick={handleClose}
      >
        <p>{t('orderForm.orderConfirmationModal.bodyLine1')}</p>
        <p>{t('orderForm.orderConfirmationModal.bodyLine2')}</p>
        <p>{t('orderForm.orderConfirmationModal.bodyLine3')}</p>
      </Prompt>
    </StyledModal>
  );
};

export default OrderConfirmationModal;
